import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Users, UserCheck, TrendingUp, Briefcase, FileText, AlertCircle, X, History, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "./adminApi";

function StatCard({ icon: Icon, label, value, accent = "#662483", testid, onClick, subtitle }) {
  const clickable = !!onClick;
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={!clickable}
      className={`bg-white border border-gray-200 p-5 flex items-start gap-4 text-left w-full transition-all ${clickable ? "hover:border-[#662483] hover:shadow-md cursor-pointer group" : "cursor-default"}`}
    >
      <div className="w-11 h-11 flex items-center justify-center" style={{ backgroundColor: `${accent}15`, color: accent }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1">
          {label}
          {clickable && <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />}
        </p>
        <p className="text-3xl font-black text-[#3F3F3F] mt-1 leading-none">{value}</p>
        {subtitle && <p className="text-[11px] text-gray-400 mt-1">{subtitle}</p>}
      </div>
    </button>
  );
}

export default function StatsTab({ onNavigateToCandidates, onNavigateToOffers, onNavigateToApplications }) {
  const navigateToCandidates = (filters) => onNavigateToCandidates?.(filters);
  const navigateToOffers = () => onNavigateToOffers?.();
  const navigateToApplications = () => onNavigateToApplications?.();
  const [stats, setStats] = useState(null);
  const [recentViews, setRecentViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bannerDismissedAt, setBannerDismissedAt] = useState(
    () => parseInt(localStorage.getItem("email_error_banner_dismissed_count") || "0", 10)
  );

  useEffect(() => {
    (async () => {
      try {
        const [s, rv] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/recent-views?limit=5").catch(() => ({ data: [] })),
        ]);
        setStats(s.data);
        setRecentViews(rv.data || []);
      } catch {
        toast.error("Impossible de charger les statistiques");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="text-center py-16 text-gray-500">Chargement…</div>;
  if (!stats) return <div className="text-center py-16 text-gray-500">Aucune donnée</div>;

  const kpis = [
    { icon: Users, label: "Total inscrits", value: stats.candidates.total, accent: "#662483", testid: "kpi-candidates-total", onClick: () => navigateToCandidates({ role: "all", status: "all", in_mission: "all", last_login: "all" }) },
    { icon: UserCheck, label: "En mission", value: stats.candidates.in_mission, accent: "#0069B4", testid: "kpi-in-mission", onClick: () => navigateToCandidates({ role: "employee", status: "active", in_mission: "all", last_login: "all" }) },
    { icon: TrendingUp, label: "Taux de transformation", value: `${stats.candidates.transform_rate}%`, accent: "#E5CD96", testid: "kpi-transform-rate", subtitle: "salariés / inscrits" },
    { icon: TrendingUp, label: "Nouveaux cette semaine", value: stats.candidates.new_week, accent: "#662483", testid: "kpi-new-week" },
    { icon: TrendingUp, label: "Nouveaux ce mois", value: stats.candidates.new_month, accent: "#0069B4", testid: "kpi-new-month" },
    { icon: TrendingUp, label: "Nouveaux (30j)", value: stats.candidates.new_30d, accent: "#E5CD96", testid: "kpi-new-30d", onClick: () => navigateToCandidates({ role: "all", status: "active", in_mission: "all", last_login: "<30" }) },
    { icon: Briefcase, label: "Offres actives", value: stats.offers.active, accent: "#662483", testid: "kpi-offers-active", onClick: navigateToOffers },
    { icon: FileText, label: "Candidatures", value: stats.applications.total, accent: "#0069B4", testid: "kpi-applications-total", onClick: navigateToApplications },
    { icon: TrendingUp, label: "Candidatures (30j)", value: stats.applications.last_30d, accent: "#E5CD96", testid: "kpi-applications-30d", onClick: navigateToApplications },
  ];

  const maxCount = Math.max(1, ...stats.applications_per_offer.map((x) => x.count));

  const formatRelative = (iso) => {
    if (!iso) return "—";
    const diff = Date.now() - Date.parse(iso);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "à l'instant";
    if (mins < 60) return `il y a ${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `il y a ${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `il y a ${days} j.`;
    return new Date(iso).toLocaleDateString("fr-FR");
  };

  return (
    <div data-testid="stats-tab" className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {kpis.map((k) => <StatCard key={k.label} {...k} />)}
      </div>

      {stats.emails.error > 0 && stats.emails.error > bannerDismissedAt && (
        <div className="bg-red-50 border border-red-200 p-4 flex items-center gap-3" data-testid="emails-error-banner">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700 flex-1">
            <strong>{stats.emails.error}</strong> emails en erreur. Consultez l'onglet <strong>Configuration → Logs Emails</strong> pour les renvoyer.
          </p>
          <button
            onClick={() => {
              localStorage.setItem("email_error_banner_dismissed_count", String(stats.emails.error));
              setBannerDismissedAt(stats.emails.error);
            }}
            className="p-1 hover:bg-red-100 text-red-600 transition-colors"
            title="Masquer cette alerte"
            data-testid="dismiss-email-banner"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Recently viewed candidates */}
      <div className="bg-white border border-gray-200 p-6" data-testid="recent-views-widget">
        <h3 className="text-lg font-bold text-[#3F3F3F] mb-4 flex items-center gap-2">
          <History className="w-5 h-5 text-[#662483]" /> Derniers candidats consultés
        </h3>
        {recentViews.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center italic">Aucune consultation récente. Ouvrez un profil candidat depuis l'onglet Candidats pour le voir apparaître ici.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {recentViews.map((rv) => (
              <button
                key={rv.user_id}
                type="button"
                onClick={() => navigateToCandidates({ role: "all", status: "all" })}
                className="w-full flex items-center gap-3 py-3 hover:bg-[#F0E9F3]/40 px-2 -mx-2 transition-colors text-left group"
                data-testid={`recent-view-${rv.user_id}`}
              >
                <div className={`w-8 h-8 flex items-center justify-center text-xs font-bold ${rv.user_role === "employee" ? "bg-[#F0E9F3] text-[#662483]" : "bg-[#E6F0F7] text-[#0069B4]"}`}>
                  {(rv.user_name || "?").substring(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#3F3F3F] truncate">{rv.user_name || "(Sans nom)"}</p>
                  <p className="text-[11px] text-gray-500 truncate">{rv.user_email} · {rv.user_role === "employee" ? "Salarié" : "Candidat"}</p>
                </div>
                <span className="text-[11px] text-gray-400 shrink-0">{formatRelative(rv.viewed_at)}</span>
                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#662483] transition-colors shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-[#3F3F3F] mb-4 flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-[#662483]" /> Candidatures par offre
        </h3>
        {stats.applications_per_offer.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">Aucune candidature pour le moment.</p>
        ) : (
          <div className="space-y-3">
            {stats.applications_per_offer.map((o) => (
              <div key={o.offer_id} data-testid={`apps-per-offer-${o.offer_number}`} className="flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-[#662483]">{o.offer_number}</p>
                  <p className="text-sm font-medium text-[#3F3F3F] truncate">{o.title}</p>
                </div>
                <div className="flex-1 h-2 bg-gray-100 relative">
                  <div className="h-full bg-gradient-to-r from-[#662483] to-[#0069B4]" style={{ width: `${(o.count / maxCount) * 100}%` }} />
                </div>
                <div className="w-16 text-right">
                  <span className="text-lg font-black text-[#3F3F3F]">{o.count}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 p-6" data-testid="per-agency-kpis">
        <h3 className="text-lg font-bold text-[#3F3F3F] mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-[#0069B4]" /> KPIs par agence
        </h3>
        {(!stats.per_agency || stats.per_agency.length === 0) ? (
          <p className="text-sm text-gray-500 py-6 text-center">Aucune agence renseignée pour le moment.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-gray-500 text-left border-b border-gray-200">
                  <th className="py-2 pr-3">Agence</th>
                  <th className="py-2 pr-3 text-right">Inscrits</th>
                  <th className="py-2 pr-3 text-right">En mission</th>
                  <th className="py-2 pr-3 text-right">Nouveaux (30j)</th>
                  <th className="py-2 pr-3 text-right">Transfo.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stats.per_agency.map((a) => {
                  const filterAgencyValue = a.agency_id || "__none__";
                  return (
                    <tr
                      key={a.agency_id || "none"}
                      onClick={() => navigateToCandidates({ role: "all", status: "all", in_mission: "all", last_login: "all", agency_id: filterAgencyValue })}
                      className="cursor-pointer hover:bg-[#F0E9F3] transition-colors group"
                      data-testid={`agency-row-${a.agency_id || "none"}`}
                    >
                      <td className="py-2 pr-3">
                        <p className="font-semibold text-[#3F3F3F] group-hover:text-[#662483] flex items-center gap-2">
                          {a.name}
                          <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                        </p>
                        {a.city && <p className="text-[11px] text-gray-500">{a.city}{a.email ? ` · ${a.email}` : ""}</p>}
                      </td>
                      <td className="py-2 pr-3 text-right font-bold text-[#662483]">{a.total}</td>
                      <td className="py-2 pr-3 text-right font-bold text-[#0069B4]">{a.in_mission}</td>
                      <td className="py-2 pr-3 text-right text-gray-700">{a.new_30d}</td>
                      <td className="py-2 pr-3 text-right">
                        <Badge className="bg-[#F0E9F3] text-[#662483] rounded-none font-bold">{a.transform_rate}%</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
